export interface ParallaxOptions {
  tileWidthPx?: number;
  layerSelector?: string;
  trackSelector?: string;
  defaultDurationSec?: number;
}

interface LayerState {
  trackEl: HTMLElement;
  offsetPx: number;
  pxPerMs: number;
  tileWidthPx: number;
}

export interface ParallaxController {
  stop: () => void;
  pause?: () => void;
  resume?: () => void;
  isPaused?: () => boolean;
}

export function initCssParallax(parallaxEl: HTMLElement, options: ParallaxOptions = {}): ParallaxController {
  const layerSelector = options.layerSelector ?? '.layer';
  const trackSelector = options.trackSelector ?? '.layer__track';
  const defaultDurationSec = options.defaultDurationSec ?? 10;

  const rootStyles = getComputedStyle(document.documentElement);
  const cssTileWidth = parseFloat(rootStyles.getPropertyValue('--tile-width'));
  const tileWidthPx = Number.isFinite(options.tileWidthPx ?? cssTileWidth)
    ? (options.tileWidthPx ?? cssTileWidth)
    : 576;

  const layerEls = Array.from(parallaxEl.querySelectorAll<HTMLElement>(layerSelector));
  const states: LayerState[] = [];

  for (const layerEl of layerEls) {
    const trackEl = layerEl.querySelector<HTMLElement>(trackSelector);
    if (!trackEl) continue;
    const dataDurationSec = parseFloat(layerEl.dataset['duration'] || '');
    const durationMs = Number.isFinite(dataDurationSec) && dataDurationSec > 0
      ? dataDurationSec * 1000
      : defaultDurationSec * 1000;
    states.push({
      trackEl,
      offsetPx: 0,
      pxPerMs: tileWidthPx / durationMs,
      tileWidthPx
    });
  }

  let rafId = 0;
  let prevTs = performance.now();
  let paused = false;
  const tick = (now: number) => {
    if (paused) return;
    const dt = now - prevTs;
    prevTs = now;
    for (const s of states) {
      s.offsetPx += dt * s.pxPerMs;
      if (s.offsetPx >= s.tileWidthPx) {
        s.offsetPx = s.offsetPx % s.tileWidthPx;
      }
      s.trackEl.style.transform = `translate3d(${-s.offsetPx}px, 0, 0)`;
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  const onPageShow = () => {
    layerEls.forEach((layerEl, idx) => {
      const dataSec = parseFloat(layerEl.dataset['duration'] || '');
      if (Number.isFinite(dataSec) && dataSec > 0 && states[idx]) {
        states[idx].pxPerMs = tileWidthPx / (dataSec * 1000);
      }
    });
  };
  window.addEventListener('pageshow', onPageShow);

  const stop = () => {
    paused = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener('pageshow', onPageShow);
  };
  const pause = () => {
    if (paused) return;
    paused = true;
    try {
      cancelAnimationFrame(rafId);
    } catch {
      // Игнорируем ошибку отмены rAF — в худшем случае кадр просто дорисуется
    }
  };
  const resume = () => {
    if (!paused) return;
    paused = false;
    prevTs = performance.now();
    rafId = requestAnimationFrame(tick);
  };
  const isPaused = () => paused;

  return { stop, pause, resume, isPaused };
}
